import { useEffect, useState, useSyncExternalStore } from 'react';
import { useSelector } from 'react-redux';

/**
 * ZKTeco Device Sync visibility (owner decision 2026-09-29): the Hr/Admin "Device Sync" entry and the
 * `/attendance/device-sync` route exist only for a company with a (non-archived) ZKTeco connection.
 * `Account/GetUserInfo` reports it as `company.zktecoConnected` (stamped for Hr/Admin only).
 *
 * Same shape as utils/hourlyPayAccess.ts, for the same reason: the flag is recorded where every GetUserInfo
 * response already passes (`getUserInfoCoalesced`) instead of widening the redux user slice, whose inferred
 * type the tsc baseline keys on. A per-user localStorage copy lets a returning session render the right nav
 * before the boot request lands; the live response always overwrites it.
 *
 * ⚠️ AN AFFORDANCE, NEVER A BOUNDARY. Every ZKTeco endpoint keeps its own role and company checks.
 */

interface CachedFlag {
  userId: string;
  connected: boolean;
  /** True once a LIVE GetUserInfo response has been recorded this page load (not just the cache). */
  live: boolean;
}

const STORAGE_KEY = 'mowazaf.zktecoConnected.v1';

let current: CachedFlag | null = readCache();
const listeners = new Set<() => void>();

function readCache(): CachedFlag | null {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CachedFlag>;
    if (typeof parsed.userId !== 'string' || !parsed.userId) return null;
    return { userId: parsed.userId, connected: parsed.connected === true, live: false };
  } catch {
    return null;
  }
}

function writeCache(value: CachedFlag): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ userId: value.userId, connected: value.connected }));
  } catch {
    /* storage full / disabled: the in-memory copy still serves this session */
  }
}

/** Called with every GetUserInfo payload. Never throws (it sits inside the boot request's promise chain). */
export function recordZktecoFlag(payload: unknown): void {
  try {
    if (!payload || typeof payload !== 'object') return;
    const user = payload as { id?: unknown; company?: { zktecoConnected?: unknown } | null };
    if (typeof user.id !== 'string' || !user.id) return;
    const next: CachedFlag = { userId: user.id, connected: user.company?.zktecoConnected === true, live: true };
    if (current && current.live && current.userId === next.userId && current.connected === next.connected) return;
    current = next;
    writeCache(next);
    listeners.forEach((listener) => {
      try {
        listener();
      } catch {
        /* one broken subscriber must not starve the others */
      }
    });
  } catch {
    /* see above */
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function snapshot(): CachedFlag | null {
  return current;
}

export interface ZktecoAccess {
  /** The company has a ZKTeco connection. Absent / unknown reads as false (fail closed). */
  connected: boolean;
  /** True once a live GetUserInfo response confirmed the flag this page load. */
  live: boolean;
}

export function useZktecoAccess(): ZktecoAccess {
  const userId = useSelector((state: { user?: { userId?: string | null } }) => state.user?.userId ?? null);
  const flag = useSyncExternalStore(subscribe, snapshot, snapshot);
  const mine = flag && userId && flag.userId === userId ? flag : null;
  return { connected: mine?.connected === true, live: mine?.live === true };
}

/** How long the route guard waits for the live flag before deciding with "unknown" (= not connected). */
export const ZKTECO_FLAG_MAX_WAIT_MS = 15000;

export function useZktecoFlagSettled(live: boolean): boolean {
  const [timedOut, setTimedOut] = useState(false);
  useEffect(() => {
    if (live) return;
    const timer = setTimeout(() => setTimedOut(true), ZKTECO_FLAG_MAX_WAIT_MS);
    return () => clearTimeout(timer);
  }, [live]);
  return live || timedOut;
}
